<template>
  <div class="gallery2">
    <link v-if="items[0]" rel="preload" as="image" :href="items[0]?.url" />
    <div v-if="items.length > 1">
      <Carousel ref="gallery" :settings="carouselSettings" v-model="modelValue">
        <template #slides>
          <Slide v-for="(item, index) in items" :key="item">
            <div
              v-if="item.type == 'Vimeo'"
              class="iframe-container print-hidden"
              :id="'player-' + index"
            >
              <VimeoPlayer
                :ref="'player-' + index"
                :video-id="item.videoId.toString()"
                :player-height="150"
                :options="{ quality: '720p', dnt: 'true' }"
                :controls="showControls"
                @play="vimeoPlaying"
                @pause="vimeoPaused"
              ></VimeoPlayer>
            </div>
            <div
              v-if="item.type == 'Youtube'"
              class="iframe-container iframe-container--overlay-none print-hidden"
            >
              <iframe
                width="100%"
                height="100%"
                frameborder="0"
                :src="
                  'https://www.youtube-nocookie.com/embed/' +
                  item.videoId +
                  '?autoplay=0&fs=0&iv_load_policy=3&showinfo=0&rel=0&cc_load_policy=0&enablejsapi=1'
                "
                id="ytplayer"
                :ref="'ytplayer'"
              ></iframe>
            </div>
            <img
              v-if="item.type == 'Image'"
              :loading="index === 0 ? undefined : 'lazy'"
              :fetchpriority="index === 0 ? 'high' : undefined"
              :src="item.url"
              :alt="item.alt"
              @error="imageError"
              class="carousel__item gallery2__image print-hidden"
            />
          </Slide>
        </template>
        <template #addons="{ slidesCount, currentSlide }">
          <!-- <Navigation v-if="slidesCount > 1" /> -->
          <div
            ref="galleryLeft"
            class="gallery2__navigation gallery2__navigation--left"
          >
            <Arrow direction="left" :invert="true" loading="eager" />
          </div>
          <div
            ref="galleryRight"
            class="gallery2__navigation gallery2__navigation--right"
          >
            <Arrow direction="right" :invert="true" loading="eager" />
          </div>
          <div class="gallery2__index-count print-hidden">
            {{ currentSlide + 1 }} / {{ slidesCount }}
          </div>

          <div
            class="gallery2__button-container gallery2__button-container--fullscreen"
            v-if="items[currentSlide].type === 'Image' && !isFullscreen"
          >
            <button
              class="gallery2__button gallery2__button--default gallery2__button--fullscreen"
              @click="openGalleryAsFullscreen"
              @mousedown.passive="activateDrag"
              @mousemove.passive="handleDrag"
              @touchstart.passive="activateDrag"
              @touchmove.passive="handleDrag"
            ></button>
          </div>

          <div
            v-if="items[currentSlide].type === 'Vimeo'"
            class="gallery2__button-container gallery2__button-container--video"
            @click="vimeoTogglePlayOnClick($event, currentSlide)"
            @mousedown.passive="activateDrag"
            @mousemove.passive="handleDrag"
          >
            <button
              :ref="'player_overlay-' + currentSlide"
              class="gallery2__button gallery2__button--default gallery2__button--play"
            ></button>
          </div>
        </template>
      </Carousel>
      <div
        v-if="isFullscreen"
        class="gallery2__button-container gallery2__button-container--close"
      >
        <button
          class="gallery2__button gallery2__button--close"
          @click="closeGalleryAsFullscreen"
          @mousedown.passive="activateDrag"
          @mousemove.passive="handleDrag"
        >
          <span class="icon--plus icon--rotate"></span>
        </button>
      </div>
      <div
        v-if="firstAvailableImage"
        class="gallery2__print-image hidden print-visible"
      >
        <img
          loading="lazy"
          :src="firstAvailableImage.url"
          :alt="firstAvailableImage.alt"
          @error="imageError"
          class="image"
        />
      </div>
    </div>
    <div
      v-if="items.length == 1 && items[0].type == 'Vimeo'"
      class="gallery2__solo--video print-hidden"
    >
      <div class="gallery2__solo-container">
        <VimeoPlayer
          ref="player__solo"
          :video-id="items[0].videoId"
          :player-height="150"
          :options="{ quality: '720p', dnt: 'true' }"
          :controls="showControls"
        ></VimeoPlayer>
      </div>
    </div>
    <div
      v-if="items.length == 1 && items[0].type == 'Youtube'"
      class="gallery2__solo--video print-hidden"
    >
      <div class="gallery2__solo-container">
        <iframe
          width="100%"
          height="100%"
          frameborder="0"
          :src="
            'https://www.youtube-nocookie.com/embed/' +
            items[0].videoId +
            '?autoplay=0&fs=0&iv_load_policy=3&showinfo=0&rel=0&cc_load_policy=0&enablejsapi=1'
          "
          id="ytplayer"
        ></iframe>
      </div>
    </div>
    <div
      v-if="items.length == 1 && items[0].type == 'Image'"
      class="gallery2__solo--image"
    >
      <img
        :src="items[0].url"
        :alt="items[0].alt"
        class="image"
        @click="openGalleryAsFullscreen"
        @error="imageError"
      />
      <div
        class="gallery2__button-container gallery2__button-container--fullscreen"
        v-if="!isFullscreen"
      >
        <button
          class="gallery2__button gallery2__button--default gallery2__button--fullscreen"
          @click="openGalleryAsFullscreen"
          @mousedown.passive="activateDrag"
          @mousemove.passive="handleDrag"
        ></button>
      </div>
      <div
        v-if="isFullscreen"
        class="gallery2__button-container gallery2__button-container--close"
      >
        <button
          class="gallery2__button gallery2__button--close"
          @click="closeGalleryAsFullscreen"
          @mousedown.passive="activateDrag"
          @mousemove.passive="handleDrag"
        >
          <span class="icon--plus icon--rotate"></span>
        </button>
      </div>
    </div>
    <div v-if="items.length == 0" class="gallery2__solo--image">
      <img src class="fallback-image" />
    </div>
    <div
      ref="galleryOverlay"
      class="gallery2__overlay"
      v-show="!hideOverlay"
    ></div>
  </div>
</template>
<script>
import { Carousel, Slide } from 'vue3-carousel'
import { vueVimeoPlayer as VimeoPlayer } from 'vue-vimeo-player'
import { getParent } from '@/functions'

import Arrow from '@shared/Arrow/Arrow.vue'

export default {
  components: {
    Carousel,
    Slide,
    VimeoPlayer,
    Arrow,
  },
  props: {
    items: { default: null, type: Array },
  },
  data() {
    return {
      modelValue: 0,
      showControls: true,
      activeOverlay: null,
      activeVideoPlayer: null,
      activeGalleryImageUrl: null,
      firstAvailableImage: null,
      isFullscreen: false,
      ytplayer: null,
      carouselSettings: {
        itemsToShow: 1,
        wrapAround: true,
        snapAlign: 'center',
        touchDrag: true,
        mouseDrag: true,
      },
      dragActive: false,
      dragXStart: null,
      dragThreshold: 150,
      ignoreKeyup: false,
      videoIsPlaying: false,
      hideOverlay: false,
    }
  },
  methods: {
    init() {
      if (this.items.length > 0) {
        this.calculateDragThreshold()
        this.initiateListeners()
        this.getFirstImageInGallery()
      }
    },
    ///***** Calculating the dragThreshold *****///
    /// Max dragThreshold is 150px, but with smaller devices like phone, this might be to much
    /// 20% of the screensize (hence the dividing by 5) would work better
    /// Most of the small high resolution devices use a pixelRatio, which will used for dividing aswell to mach the actual 'distance'
    calculateDragThreshold() {
      /* Formula */
      // (browserWidth / pixelRation) * 20%
      const calculatedThreshold =
        document.body.offsetWidth / window.devicePixelRatio / 5
      this.dragThreshold = calculatedThreshold > 150 ? 150 : calculatedThreshold
    },
    checkForYoutubeVideo() {
      if (this.items[this.modelValue].type == 'Youtube') {
        this.hideOverlay = true
      } else if (this.hideOverlay) {
        this.hideOverlay = false
      }
    },
    vimeoTogglePlayOnClick(evt, currentIndex) {
      this.releaseDrag()
      if (this.videoIsPlaying) {
        this.vimeoStopPlaying()
      } else {
        this.$refs['player-' + currentIndex][0].play()
      }
    },
    vimeoStopPlaying() {
      this.$refs['player-' + this.modelValue][0].pause()
    },
    vimeoPlaying() {
      this.videoIsPlaying = true
      this.$refs['player_overlay-' + this.modelValue].style.display = 'none'
    },
    vimeoPaused() {
      this.videoIsPlaying = false
      this.$refs['player_overlay-' + this.modelValue].style.display = 'block'
    },
    youtubeStopPlaying() {
      this.ytplayer.pauseVideo()
    },
    getFirstImageInGallery() {
      const itemList = this.items.filter(itemProp => {
        return itemProp.type === 'Image'
      })
      if (itemList.length > 0) {
        this.firstAvailableImage = itemList[0]
      }
    },
    openGalleryAsFullscreen(event) {
      this.dragActive = false
      if (getParent(event.target, 'carousel__slide--active' === null)) {
        return
      }

      this.isFullscreen = true
      document.body.classList.add('isGalleryFullscreen')
      this.carouselSettings.snapAlign = 'start'

      window.history.pushState(null, '', null)

      window.addEventListener('popstate', this.closeGalleryAsFullscreen)
    },
    closeGalleryAsFullscreen(isPopstate = false) {
      this.dragActive = false
      this.isFullscreen = false
      document.body.classList.remove('isGalleryFullscreen')
      this.carouselSettings.snapAlign = 'center'

      if (!isPopstate) {
        window.history.back()
      }

      window.removeEventListener('popstate', this.closeOverlay)
    },
    imageError(event) {
      event.target.attributes.class.value = 'fallback-image image'
      return
    },
    handleResize() {
      this.calculateDragThreshold()
    },
    initiateListeners() {
      window.addEventListener('resize', this.handleResize)
      document.addEventListener(
        'keyup',
        this.handleGalleryKeyboardEvents,
        false
      )
    },
    removeListeners() {
      window.removeEventListener('resize', this.handleResize)
      document.removeEventListener(
        'keyup',
        this.handleGalleryKeyboardEvents,
        false
      )
    },
    handleGalleryKeyboardEvents(event) {
      switch (event.keyCode) {
        case 39: // right arrow key
          this.galleryNext()
          break
        case 37: // left arrow key
          this.galleryPrev()
          break
        case 27: // escape for exiting fullscreen
          if (!this.isFullscreen) break
          this.closeGalleryAsFullscreen()
          break
      }
    },
    galleryNext() {
      if (this.$refs.gallery !== undefined) {
        this.stopVideoOnSlide()
        if (this.modelValue === this.items.length - 1) {
          this.modelValue = 0
        } else {
          this.modelValue += 1
        }
        this.checkForYoutubeVideo()
      }
    },
    galleryPrev() {
      if (this.$refs.gallery !== undefined) {
        this.stopVideoOnSlide()
        if (this.modelValue === 0) {
          this.modelValue = this.items.length - 1
        } else {
          this.modelValue -= 1
        }
        this.checkForYoutubeVideo()
      }
    },
    stopVideoOnSlide() {
      if (!this.videoIsPlaying) return

      if (this.items[this.modelValue].type === 'Vimeo') {
        this.vimeoStopPlaying()
      } else if (
        this.items[this.modelValue].type === 'Youtube' &&
        this.ytplayer
      ) {
        this.youtubeStopPlaying()
      }
    },
    setupYoutubeVideo() {
      if (this.items.find(i => i.type == 'Youtube') === undefined) return

      // setup script tag for youtube
      var tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      var firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

      window.onYouTubeIframeAPIReady = () => {
        this.ytplayer = new window.YT.Player('ytplayer', {
          events: {
            onStateChange: this.onYoutubePlayerStateChange,
          },
        })
      }
    },
    onYoutubePlayerStateChange(event) {
      if (event.data === -1 || event.data === 1) {
        this.videoIsPlaying = true
      } else if (event.data === 2) {
        this.videoIsPlaying = false
      }
    },
    activateDrag(event) {
      this.dragActive = true
      this.ignoreKeyup = false
      this.dragXStart =
        event.touches === undefined ? event.clientX : event.touches[0].clientX
    },
    releaseDrag() {
      this.dragActive = false
    },
    onNavigationMouseUp(event) {
      this.dragActive = false
      if (this.ignoreKeyup) {
        this.ignoreKeyup = false
      } else {
        if (
          event.currentTarget.classList.contains('gallery2__navigation--left')
        ) {
          this.galleryPrev()
        } else if (
          event.currentTarget.classList.contains('gallery2__navigation--right')
        ) {
          this.galleryNext()
        }
      }
    },
    handleDrag(event) {
      if (this.dragActive) {
        const currentX =
          event.touches === undefined ? event.clientX : event.touches[0].clientX
        if (currentX < this.dragXStart - this.dragThreshold) {
          this.releaseDrag()
          this.ignoreKeyup = true
          this.galleryNext()
        } else if (currentX > this.dragXStart + this.dragThreshold) {
          this.releaseDrag()
          this.ignoreKeyup = true
          this.galleryPrev()
        }
      }
    },
    addElementEvents(element, isNavigation = false) {
      if (element === undefined) return

      // Mouse events
      element.addEventListener('mousedown', this.activateDrag)
      element.addEventListener('mousemove', this.handleDrag)
      element.addEventListener(
        'mouseup',
        isNavigation ? this.onNavigationMouseUp : this.releaseDrag
      )

      // Touch events
      element.addEventListener('touchstart', this.activateDrag, {
        passive: true,
      })
      element.addEventListener('touchmove', this.handleDrag, { passive: true })
    },
    removeElementEvents(element, isNavigation = false) {
      if (element === undefined) return

      element.removeEventListener('mousedown', this.activateDrag)
      element.removeEventListener('mousemove', this.handleDrag)
      element.removeEventListener(
        'mouseup',
        isNavigation ? this.onNavigationMouseUp : this.releaseDrag
      )

      // Touch events
      element.removeEventListener('touchstart', this.activateDrag)
      element.removeEventListener('touchmove', this.handleDrag)
    },
    bindEvents() {
      this.addElementEvents(this.$refs.galleryOverlay)
      this.addElementEvents(this.$refs.galleryLeft, true)
      this.addElementEvents(this.$refs.galleryRight, true)
    },
    unbindEvents() {
      this.removeElementEvents(this.$refs.galleryOverlay)
      this.removeElementEvents(this.$refs.galleryLeft, true)
      this.removeElementEvents(this.$refs.galleryRight, true)
    },
  },
  created() {
    this.init()
    this.setupYoutubeVideo()
  },
  mounted() {
    this.checkForYoutubeVideo()

    this.bindEvents()
  },
  unmounted() {
    this.removeListeners()

    this.unbindEvents()
  },
}
</script>

<style lang="scss">
@import 'Gallery2.scss';
@import 'Gallery.scss';
@import 'Vue3Carousel.css';
</style>
